<template>
  <header :class="['topbar', themeClass]">
    <div class="topbar-izquierda">
      <h5 class="topbar-titulo">{{ pageTitle }}</h5>
    </div>

    <div class="topbar-derecha">
      <!-- Botón de tema -->
      <button
        type="button"
        class="topbar-btn"
        @click="toggleTheme"
        :title="isDarkMode ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'"
      >
        <i :class="isDarkMode ? 'bi bi-sun-fill' : 'bi bi-moon-fill'"></i>
      </button>

      <!-- Menú de usuario -->
      <div class="topbar-usuario" ref="menuUsuario">
        <button type="button" class="topbar-usuario__btn" @click="toggleDropdown">
          <span class="topbar-usuario__avatar"><i class="bi bi-person-fill"></i></span>
          <span class="topbar-usuario__nombre">{{ usuarioNombre }}</span>
          <i class="bi bi-chevron-down topbar-usuario__chevron" :class="{ 'topbar-usuario__chevron--abierto': dropdownOpen }"></i>
        </button>
        <transition name="desplegable-topbar">
          <div v-show="dropdownOpen" class="topbar-menu">
            <router-link to="/usuarios" class="topbar-menu__item" @click="closeDropdown">
              <i class="bi bi-person"></i> Perfil
            </router-link>
            <router-link to="/usuarios" class="topbar-menu__item" @click="closeDropdown">
              <i class="bi bi-key"></i> Cambiar contraseña
            </router-link>
            <div class="topbar-menu__separador"></div>
            <button type="button" class="topbar-menu__item topbar-menu__item--peligro" @click.prevent="cerrarSesion">
              <i class="bi bi-box-arrow-right"></i> Cerrar sesión
            </button>
          </div>
        </transition>
      </div>
    </div>
  </header>
</template>

<script>
import themeManager from '@/store/theme'
import { mapGetters } from 'vuex'
import auth from '@/services/servicioAutenticacion'

export default {
  name: 'AdminTopbar',
  data() {
    return {
      empresaNombre: '',
      dropdownOpen: false
    };
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  computed: {
    ...mapGetters(['currentUser']),
    themeClass() {
      return themeManager.themeClass
    },
    isDarkMode() {
      return themeManager.isDarkMode
    },
    usuarioNombre() {
      return this.currentUser?.name || this.currentUser?.email || 'Admin'
    },
    pageTitle() {
      const routeName = this.$route.name;
      
      // Mapeo de rutas a títulos
      const titleMap = {
        'AdminDashboard': 'Dashboard Principal',
        'AdminEmpresas': 'Gestión de Empresas',
        'AdminEmpresaNueva': 'Nueva Empresa',
        'AdminEmpresaSubdominios': 'Subdominios',
        'AdminModulos': 'Módulos Disponibles',
        'AdminModulosPermisos': 'Permisos de Módulos',
        'AdminModulosActivacion': 'Activación de Módulos',
        'AdminFacturacionSuscripciones': 'Suscripciones',
        'AdminFacturacionPagos': 'Pagos',
        'AdminFacturacionPlanes': 'Planes de Suscripción',
        'AdminReportesEmpresas': 'Estadísticas de Empresas',
        'AdminReportesIngresos': 'Reportes de Ingresos',
        'AdminReportesUso': 'Reportes de Uso',
        'AdminConfiguracionUsuarios': 'Gestión de Usuarios',
  'AdminConfiguracionParametros': 'Parámetros del Sistema'
      };
      
      return titleMap[routeName] || 'SICECOM Admin';
    }
  },
  methods: {
    toggleTheme() {
      themeManager.toggleTheme()
    },
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen
    },
    closeDropdown() {
      this.dropdownOpen = false
    },
    handleClickOutside(event) {
      const menu = this.$refs.menuUsuario
      if (this.dropdownOpen && menu && !menu.contains(event.target)) {
        this.dropdownOpen = false
      }
    },
    cerrarSesion() {
      this.$store.commit('clearAuth');
      auth.cerrarSesion()
      this.$router.push('/iniciar-sesion');
    }
  }
};
</script>

<style>
/* ===== TOPBAR ===== */
.topbar {
  background: var(--secondary-bg-main);
  border-bottom: 1px solid var(--border-main);
  height: 68px;
  position: sticky;
  top: 0;
  z-index: 1030;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  transition: background 0.3s ease, border-color 0.3s ease;
}

.topbar-titulo {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.topbar-derecha {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

/* Botón tema */
.topbar-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid var(--border-main);
  background: transparent;
  color: var(--text-secondary);
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease;
}

.topbar-btn:hover {
  background: var(--bg-main);
  color: var(--text-primary);
  border-color: var(--border-focus);
}

/* Menú usuario */
.topbar-usuario {
  position: relative;
}

.topbar-usuario__btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  border-radius: 10px;
  border: 1px solid var(--border-main);
  background: transparent;
  color: var(--text-secondary);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease;
}

.topbar-usuario__btn:hover {
  background: var(--bg-main);
  color: var(--text-primary);
}

.topbar-usuario__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: rgb(14 165 233 / 0.12);
  color: var(--primary-main);
  font-size: 0.85rem;
}

.topbar-usuario__nombre {
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.topbar-usuario__chevron {
  font-size: 0.68rem;
  opacity: 0.6;
  transition: transform 0.2s ease;
}

.topbar-usuario__chevron--abierto {
  transform: rotate(180deg);
}

/* Menú desplegable */
.topbar-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 200px;
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 12px;
  box-shadow: var(--shadow-md);
  padding: 6px;
  z-index: 1040;
}

.topbar-menu__item {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  padding: 9px 12px;
  border-radius: 8px;
  border: 0;
  background: transparent;
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  text-align: left;
}

.topbar-menu__item:hover {
  background: var(--bg-main);
  color: var(--text-primary);
}

.topbar-menu__item--peligro {
  color: var(--accent-danger);
}

.topbar-menu__item--peligro:hover {
  background: rgb(239 68 68 / 0.08);
  color: var(--accent-danger);
}

.topbar-menu__separador {
  height: 1px;
  background: var(--border-main);
  margin: 4px 6px;
}

/* Transición del menú */
.desplegable-topbar-enter-active,
.desplegable-topbar-leave-active {
  transition: opacity 0.18s ease, transform 0.2s ease;
  transform-origin: top right;
}

.desplegable-topbar-enter-from,
.desplegable-topbar-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-4px);
}

.desplegable-topbar-enter-to,
.desplegable-topbar-leave-from {
  opacity: 1;
  transform: scale(1) translateY(0);
}

@media (max-width: 768px) {
  .topbar-usuario__nombre { display: none; }
  .topbar { padding: 0 14px; }
}
</style>

